import { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { ArrowLeft, Save, Package, DollarSign, Warehouse, ToggleLeft, CheckCircle2 } from 'lucide-react';
import {
  createProduct,
  updateProductById,
  fetchProducts,
} from '../../../redux/slices/productSlice';
import { fetchCategories } from '../../../redux/slices/categorySlice';
import { addInventoryLog } from '../../../services/inventoryLogService';
import toast from 'react-hot-toast';
import AdminCard from "../../../components/common/AdminCard";
import AdminInput from "../../../components/common/AdminInput";
import { CustomDropdown } from "../../../components/common/CustomDropdown";

const AddProduct = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { productId } = useParams();
  const { products, loading } = useSelector((state) => state.product);
  const { categories } = useSelector((state) => state.category);

  const location = useLocation();
  const cloneData = location.state?.cloneData;

  const [formData, setFormData] = useState({
    categoryId: '',
    name: '',
    description: '',
    productCode: '',
    status: 'active',
    mrp: '',
    discount15: '',
    discount25: '',
    discount35: '',
    discount42: '',
    discount50: '',
    miniStock: '',
    stockCount: '',
    gifted: '',
    uom: 'pcs',
    volPoint: '',
    purchase: '',
    purchasePrice: '',
    clubUsed: '',
    sale: '',
    personalUsed: '',
    isSpecial: false,
  });

  const isEditMode = !!productId;

  useEffect(() => {
    dispatch(fetchCategories());
    if (isEditMode) {
      dispatch(fetchProducts());
    }
  }, [dispatch, isEditMode]);

  useEffect(() => {
    if (isEditMode && products.length > 0) {
      const product = products.find((p) => p.id === productId);
      if (product) {
        setFormData({
          categoryId: product.categoryId || '',
          name: product.name || '',
          description: product.description || '',
          productCode: product.productCode || '',
          status: product.status || 'active',
          mrp: product.mrp || '',
          discount15: product.discount15 || '',
          discount25: product.discount25 || '',
          discount35: product.discount35 || '',
          discount42: product.discount42 || '',
          discount50: product.discount50 || '',
          miniStock: product.miniStock || '',
          stockCount: product.stockCount || '',
          gifted: product.gifted || '',
          uom: product.uom || 'pcs',
          volPoint: product.volPoint || '',
          purchase: product.purchase || '',
          purchasePrice: product.purchasePrice || '',
          clubUsed: product.clubUsed || '',
          sale: product.sale || '',
          personalUsed: product.personalUsed || '',
          isSpecial: product.isSpecial || false,
        });
      }
    }
  }, [isEditMode, productId, products]);

  useEffect(() => {
    if (cloneData && !isEditMode) {
      setFormData({
        categoryId: cloneData.categoryId || '',
        name: cloneData.name ? `${cloneData.name} - Copy` : '',
        description: cloneData.description || '',
        productCode: cloneData.productCode ? `${cloneData.productCode}-COPY` : '',
        status: cloneData.status || 'active',
        mrp: cloneData.mrp || '',
        discount15: cloneData.discount15 || '',
        discount25: cloneData.discount25 || '',
        discount35: cloneData.discount35 || '',
        discount42: cloneData.discount42 || '',
        discount50: cloneData.discount50 || '',
        miniStock: cloneData.miniStock || '',
        stockCount: cloneData.stockCount || '',
        gifted: cloneData.gifted || '',
        uom: cloneData.uom || 'pcs',
        volPoint: cloneData.volPoint || '',
        purchase: cloneData.purchase || '',
        purchasePrice: cloneData.purchasePrice || '',
        clubUsed: cloneData.clubUsed || '',
        sale: cloneData.sale || '',
        personalUsed: cloneData.personalUsed || '',
        isSpecial: cloneData.isSpecial || false,
      });
    }
  }, [cloneData, isEditMode]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Auto-calculate discount prices when MRP changes
    if (name === 'mrp') {
      const mrpVal = parseFloat(value) || 0;
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        discount15: mrpVal ? (mrpVal * 0.85).toFixed(2) : '',
        discount25: mrpVal ? (mrpVal * 0.75).toFixed(2) : '',
        discount35: mrpVal ? (mrpVal * 0.65).toFixed(2) : '',
        discount42: mrpVal ? (mrpVal * 0.58).toFixed(2) : '',
        discount50: mrpVal ? (mrpVal * 0.50).toFixed(2) : '',
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleDropdownChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('Product name is required');
      return;
    }

    if (!formData.productCode.trim()) {
      toast.error('Product code is required');
      return;
    }

    if (!formData.categoryId) {
      toast.error('Please select a category');
      return;
    }

    try {
      if (isEditMode) {
        // Calculate difference for log
        const existingProduct = products.find((p) => p.id === productId);
        const oldStock = Number(existingProduct?.stockCount || 0);
        const newStock = Number(formData.stockCount || 0);
        if (oldStock !== newStock) {
          await addInventoryLog({
            productId,
            productName: formData.name,
            date: new Date().toISOString().split('T')[0],
            quantityChange: newStock - oldStock,
            previousStock: oldStock,
            newStock: newStock,
            transactionType: "Manual Adjustment",
            referenceId: "Admin Edit",
            customerName: "Admin"
          });
        }
        await dispatch(updateProductById({ productId, productData: formData })).unwrap();
        toast.success('Product updated successfully!');
      } else {
        const newProd = await dispatch(createProduct(formData)).unwrap();
        const initialStock = Number(formData.stockCount || 0);
        if (initialStock > 0) {
          await addInventoryLog({
            productId: newProd?.id || Date.now().toString(), // fallback if no ID returned
            productName: formData.name,
            date: new Date().toISOString().split('T')[0],
            quantityChange: initialStock,
            previousStock: 0,
            newStock: initialStock,
            transactionType: "Manual Adjustment",
            referenceId: "Initial Setup",
            customerName: "Admin"
          });
        }
        toast.success('Product added successfully!');
      }
      navigate('/admin/products');
    } catch (error) {
      toast.error(isEditMode ? 'Failed to update product' : 'Failed to add product');
      console.error('Error:', error);
    }
  };

  return (
    <div className='min-h-screen bg-neutral-100 p-4 md:p-6 lg:p-8 font-sans text-neutral-800'>
      <div className='space-y-6'>
        {/* Header */}
        <div className='bg-white rounded-2xl border border-neutral-200/60 shadow-sm p-4 md:p-6 mb-8'>
          <div className='flex flex-col md:flex-row justify-between items-start md:items-center gap-4'>
            <div className='flex items-center gap-4'>
              <button
                onClick={() => navigate('/admin/products')}
                className='p-2 hover:bg-neutral-100 rounded-xl transition-colors'
                title='Go Back'
              >
                <ArrowLeft className='w-5 h-5' />
              </button>
              <div>
                <h1 className='text-xl md:text-2xl font-bold text-neutral-900 tracking-tight'>
                  {isEditMode ? 'Edit Product' : 'Add New Product'}
                </h1>
                <p className='text-xs md:text-base text-neutral-500 mt-1'>
                  {isEditMode ? 'Update product details and inventory' : 'Fill in the information to list a new product in the store.'}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
          {/* Left Column - Basic & Inventory */}
          <div className='space-y-6'>
            <AdminCard title="Basic Information">
              <div className='p-4 md:p-5 space-y-4'>
                <AdminInput
                  label="Product Name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="e.g., Organic Almonds"
                  required
                />

                <AdminInput
                  label="Product Code"
                  name="productCode"
                  value={formData.productCode}
                  onChange={handleInputChange}
                  placeholder="e.g., PROD-001"
                  required
                />

                <div className="space-y-2">
                  <label className="text-sm font-medium text-neutral-700">
                    Category <span className="text-red-500">*</span>
                  </label>
                  <CustomDropdown
                    options={categories.map((category) => ({
                      value: category.id,
                      label: category.name,
                    }))}
                    value={formData.categoryId}
                    onChange={(val) => handleDropdownChange('categoryId', val)}
                    placeholder="Select a category"
                  />
                </div>

                <AdminInput
                  label="Description"
                  name="description"
                  type="textarea"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Enter a detailed description..."
                  className="mt-2"
                />
              </div>
            </AdminCard>

            <AdminCard title="Inventory Information">
              <div className='p-4 md:p-5 space-y-4'>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  <AdminInput
                    label="Stock Count"
                    name="stockCount"
                    type="number"
                    value={formData.stockCount}
                    onChange={handleInputChange}
                    placeholder="0"
                    required
                  />
                  <AdminInput
                    label="Minimum Stock"
                    name="miniStock"
                    type="number"
                    value={formData.miniStock}
                    onChange={handleInputChange}
                    placeholder="0"
                    required
                  />
                  <AdminInput
                    label="Gifted Count"
                    name="gifted"
                    type="number"
                    value={formData.gifted}
                    onChange={handleInputChange}
                    placeholder="0"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-neutral-700">
                    Unit of Measurement <span className="text-red-500">*</span>
                  </label>
                  <CustomDropdown
                    options={[
                      { value: 'pcs', label: 'Pieces (pcs)' },
                      { value: 'kg', label: 'Kilograms (kg)' },
                      { value: 'ltr', label: 'Liters (ltr)' },
                      { value: 'box', label: 'Box' },
                      { value: 'pack', label: 'Pack' },
                      { value: 'gm', label: 'Grams (gm)' },
                      { value: 'ml', label: 'Milliliters (ml)' },
                    ]}
                    value={formData.uom}
                    onChange={(val) => handleDropdownChange('uom', val)}
                    placeholder="Select UOM"
                  />
                </div>
              </div>
            </AdminCard>
          </div>

          {/* Right Column - Pricing & Status */}
          <div className='space-y-6'>
            <AdminCard title="Pricing Information">
              <div className='p-4 md:p-5 space-y-4'>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  <AdminInput
                    label="MRP (₹)"
                    name="mrp"
                    type="number"
                    step="0.01"
                    value={formData.mrp}
                    onChange={handleInputChange}
                    placeholder="0.00"
                    required
                  />
                  <AdminInput
                    label="Value Point"
                    name="volPoint"
                    type="number"
                    step="0.01"
                    value={formData.volPoint}
                    onChange={handleInputChange}
                    placeholder="0"
                  />
                </div>

                <AdminInput
                  label="Purchase Price (₹)"
                  name="purchasePrice"
                  type="number"
                  step="0.01"
                  value={formData.purchasePrice}
                  onChange={handleInputChange}
                  placeholder="0.00"
                />

                <div className='border-t border-neutral-100 pt-4 mt-4'>
                  <h4 className='text-sm font-semibold text-neutral-800 mb-4 font-sans'>Discount Pricing</h4>
                  <div className='grid grid-cols-2 md:grid-cols-3 gap-3'>
                    <AdminInput
                      label="15% OFF"
                      name="discount15"
                      type="number"
                      step="0.01"
                      value={formData.discount15}
                      onChange={handleInputChange}
                      placeholder="₹0.00"
                    />
                    <AdminInput
                      label="25% OFF"
                      name="discount25"
                      type="number"
                      step="0.01"
                      value={formData.discount25}
                      onChange={handleInputChange}
                      placeholder="₹0.00"
                    />
                    <AdminInput
                      label="35% OFF"
                      name="discount35"
                      type="number"
                      step="0.01"
                      value={formData.discount35}
                      onChange={handleInputChange}
                      placeholder="₹0.00"
                    />
                    <AdminInput
                      label="42% OFF"
                      name="discount42"
                      type="number"
                      step="0.01"
                      value={formData.discount42}
                      onChange={handleInputChange}
                      placeholder="₹0.00"
                    />
                    <AdminInput
                      label="50% OFF"
                      name="discount50"
                      type="number"
                      step="0.01"
                      value={formData.discount50}
                      onChange={handleInputChange}
                      placeholder="₹0.00"
                    />
                  </div>
                </div>
              </div>
            </AdminCard>

            <AdminCard title="Product Status">
              <div className='p-4 md:p-5 space-y-4'>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-neutral-700">
                    Status <span className="text-red-500">*</span>
                  </label>
                  <CustomDropdown
                    options={[
                      { value: 'active', label: 'Active' },
                      { value: 'disabled', label: 'Disabled' },
                    ]}
                    value={formData.status}
                    onChange={(val) => handleDropdownChange('status', val)}
                    placeholder="Select Status"
                  />
                </div>

                <div className="space-y-3 pt-2">
                  <label className="text-sm font-medium text-neutral-700">
                    Special Product
                  </label>
                  <div className="flex items-center gap-6">
                    <label className="flex items-center gap-2 cursor-pointer group">
                      <input
                        type="radio"
                        name="isSpecial"
                        checked={formData.isSpecial === true}
                        onChange={() => handleDropdownChange('isSpecial', true)}
                        className="w-4 h-4 text-red-600 border-neutral-300 focus:ring-red-500 cursor-pointer"
                      />
                      <span className="text-sm text-neutral-600 group-hover:text-neutral-900 transition-colors">Yes</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer group">
                      <input
                        type="radio"
                        name="isSpecial"
                        checked={formData.isSpecial === false}
                        onChange={() => handleDropdownChange('isSpecial', false)}
                        className="w-4 h-4 text-red-600 border-neutral-300 focus:ring-red-500 cursor-pointer"
                      />
                      <span className="text-sm text-neutral-600 group-hover:text-neutral-900 transition-colors">No</span>
                    </label>
                  </div>
                </div>
              </div>
            </AdminCard>

            <div className='pt-3 space-y-3'>
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="h-10 px-5 py-6 bg-red-700 text-white font-medium text-sm transition-all shadow-lg shadow-neutral-900/10 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed w-full rounded-xl"
              >
                {loading ? (
                  <>
                    <div className='w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin' />
                    {isEditMode ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  <>
                    <CheckCircle2 className='w-4 h-4' />
                    {isEditMode ? 'Update Product' : 'Create Product'}
                  </>
                )}
              </button>

              <button
                type='button'
                onClick={() => navigate('/admin/products')}
                className='h-10 px-5 py-6 bg-neutral-200 text-neutral-700 rounded-xl hover:bg-neutral-300 transition-colors text-sm font-medium w-full flex items-center justify-center'
              >
                Cancel
              </button>
            </div>
          </div>
        </div>

        {/* Note Card */}
        <div className='mt-8 bg-blue-50/50 border border-blue-100 rounded-2xl p-4'>
          <p className='text-xs text-blue-600 text-center leading-relaxed'>
            <strong>Note:</strong> Fields marked with <span className='text-red-500'>*</span> are required.
            Ensure pricing and stock levels are set correctly for the store summary reports.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AddProduct;